import { type FormEvent, useState } from 'react';
import { Link } from 'react-router';
import type { UserPublic } from '@mondy/shared';
import { api } from '@/api';
import { ApiError } from '@mondy/shared/api-client';
import { localTimezone } from '@/lib/format';
import { ButtonSpinner, FormField, inputClasses, primaryButtonClasses } from './FormField';
import { GoogleSignInButton } from './GoogleSignInButton';

interface Props {
  googleClientId: string | null;
  // Fires when the form enters/leaves the "check your email" confirmation
  // state so the page can hide the surrounding chrome (back link, page
  // heading, "already have an account?" footer) in that state.
  onPendingVerificationChange?: (pending: boolean) => void;
  // Invite flow plumbing — when arriving from `/accept_group_invite/:token`,
  // the signup URL carries `next`, `inviteToken`, and `email`. `email` is
  // pre-filled and disabled so the address the server was shown matches the
  // invite, and `inviteToken` is stored on the user for post-verification
  // auto-join.
  next?: string | null;
  inviteToken?: string | null;
  invitedEmail?: string | null;
}

export function SignupForm({
  googleClientId,
  onPendingVerificationChange,
  next,
  inviteToken,
  invitedEmail,
}: Props) {
  const [email, setEmail] = useState(invitedEmail ?? '');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [resendPending, setResendPending] = useState(false);
  const [resendDone, setResendDone] = useState(false);

  const emailLocked = Boolean(invitedEmail);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (password !== passwordConfirm) {
      setError('Passwords do not match');
      return;
    }
    setPending(true);
    try {
      await api.post<{ user: UserPublic; pendingVerification: true }>(
        '/api/auth/signup',
        {
          email,
          password,
          passwordConfirm,
          displayName,
          timezone: localTimezone(),
          ...(inviteToken ? { inviteToken } : {}),
        },
      );
      setSentTo(email);
      onPendingVerificationChange?.(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Sign up failed');
    } finally {
      setPending(false);
    }
  }

  async function onResend() {
    if (!sentTo) return;
    setResendPending(true);
    try {
      await api.post<{ ok: true }>('/api/auth/resend-verification', { email: sentTo });
    } catch {
      // Endpoint is intentionally non-enumerating and always 200 — ignore.
    } finally {
      setResendPending(false);
      setResendDone(true);
    }
  }

  if (sentTo) {
    return (
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-3">
          <h2 className="text-2xl font-semibold">Check your email</h2>
          <p className="text-sm text-[color:var(--color-fg-muted)]">
            We sent a verification link to <b className="text-[color:var(--color-fg)]">{sentTo}</b>.
            Click it to activate your account. The link expires in 24 hours.
          </p>
        </div>

        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={onResend}
            disabled={resendPending}
            aria-busy={resendPending}
            className={primaryButtonClasses}
          >
            {resendPending ? (
              <>
                <ButtonSpinner />
                <span>Resending…</span>
              </>
            ) : (
              'Resend verification email'
            )}
          </button>
          {resendDone ? (
            <p className="text-xs text-[color:var(--color-fg-muted)]">
              If an account with that email needs verification, we sent a new link.
            </p>
          ) : null}
        </div>

        <p className="text-center text-sm text-[color:var(--color-fg-muted)]">
          <Link
            to={
              next
                ? `/login?${new URLSearchParams({ next }).toString()}`
                : '/login'
            }
            className="text-[color:var(--color-fg)] underline underline-offset-2"
          >
            Back to login
          </Link>
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <FormField label="Display name" htmlFor="displayName">
          <input
            id="displayName"
            type="text"
            autoComplete="name"
            required
            minLength={1}
            maxLength={64}
            disabled={pending}
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            className={inputClasses}
          />
        </FormField>
        <FormField label="Email" htmlFor="email">
          <input
            id="email"
            type="email"
            autoComplete="email"
            required
            disabled={pending || emailLocked}
            readOnly={emailLocked}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputClasses}
            aria-describedby={emailLocked ? 'email-locked-hint' : undefined}
          />
          {emailLocked ? (
            <p
              id="email-locked-hint"
              className="text-xs text-[color:var(--color-fg-muted)]"
            >
              This is the address the invitation was sent to.
            </p>
          ) : null}
        </FormField>
        <FormField label="Password" htmlFor="password">
          <input
            id="password"
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            maxLength={128}
            disabled={pending}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={inputClasses}
          />
        </FormField>
        <FormField label="Confirm password" htmlFor="passwordConfirm">
          <input
            id="passwordConfirm"
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            maxLength={128}
            disabled={pending}
            value={passwordConfirm}
            onChange={(e) => setPasswordConfirm(e.target.value)}
            className={inputClasses}
          />
        </FormField>
        {error ? (
          <p className="text-sm text-[color:var(--color-danger)]">{error}</p>
        ) : null}
        <button
          type="submit"
          disabled={pending}
          aria-busy={pending}
          className={primaryButtonClasses}
        >
          {pending ? (
            <>
              <ButtonSpinner />
              <span>Creating account…</span>
            </>
          ) : (
            'Create account'
          )}
        </button>
      </form>

      <div className="flex items-center gap-3 text-xs text-[color:var(--color-fg-muted)]">
        <span className="h-px flex-1 bg-[color:var(--color-border)]" />
        or
        <span className="h-px flex-1 bg-[color:var(--color-border)]" />
      </div>

      <GoogleSignInButton
        clientId={googleClientId}
        next={next ?? null}
        inviteToken={inviteToken ?? null}
      />
    </div>
  );
}
